/* eslint-disable react/jsx-props-no-spreading */
import { FC, useEffect, useState } from 'react';
import Slider from 'react-slick';
import { useTranslations } from '@altermaind/core/i18n';
import { Asset, Box, Card, Loader, Typography, Button } from '@altermaind/web/ui';
import { useDialog, usePublicDocs } from '@/_shared/hooks';
import { DialogKeys } from '../Dialog';
import { RenderLocalesWithComponents } from '../RenderLocalesWithComponents';

type PropsArrow = {
  onClick?: () => void;
  // eslint-disable-next-line react/no-unused-prop-types
  isLast?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  closeDialog?: () => void;
  // eslint-disable-next-line react/no-unused-prop-types
  isFirst?: boolean;
};

const NextArrow: FC<PropsArrow> = ({ onClick, isLast, closeDialog }) => {
  const tShared = useTranslations('slider');
  return (
    <Button
      variant='positive'
      onClick={isLast ? closeDialog : onClick}
      style={{
        position: 'absolute',
        right: 0,
        bottom: '0',
        zIndex: 1,
      }}
      aria-label='Next'
    >
      {isLast ? tShared('close') : tShared('next')}
    </Button>
  );
};

const PrevArrow: FC<PropsArrow> = ({ onClick, isFirst }) => {
  const tShared = useTranslations('slider');

  if (isFirst) return null;

  return (
    <Button
      variant='link/primary'
      onClick={onClick}
      style={{
        position: 'absolute',
        right: 120,
        bottom: '0',
        zIndex: 1,
      }}
      aria-label='Previous'
    >
      {tShared('previous')}
    </Button>
  );
};

type Props = {
  translationsKey: string;
  dialogKey: keyof DialogKeys;
  slides: {
    title: string;
    description: string;
    assetName: string;
  }[];
  /** Optional: pass isOpen from parent to ensure sync when parent calls openDialog */
  isOpenProp?: boolean;
};

export const TutorialDialog: FC<Props> = ({ translationsKey, dialogKey, slides, isOpenProp }) => {
  const t = useTranslations(translationsKey);
  const { goToPublicsDocs } = usePublicDocs();
  // eslint-disable-next-line @altermaind/use-translations-position
  const tTutorial = useTranslations('tutorial');
  // eslint-disable-next-line @altermaind/use-translations-position
  const tShared = useTranslations('slider');
  const [loading, setLoading] = useState(true);
  const { closeDialog, Dialog, isOpen: isOpenFromHook } = useDialog(dialogKey);

  // Use prop if provided, otherwise fall back to hook value
  const isOpen = isOpenProp !== undefined ? isOpenProp : isOpenFromHook;

  const [currentSlide, setCurrentSlide] = useState(0);

  const settings = {
    dots: true,
    infinite: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    autoplaySpeed: 2000,
    arrows: true,
    pauseOnHover: true,
    pauseOnFocus: true,
    swipe: true,
    nextArrow: (
      <NextArrow
        closeDialog={closeDialog}
        isLast={currentSlide === slides.length - 1}
        onClick={undefined}
      />
    ),
    prevArrow: <PrevArrow onClick={undefined} isFirst={currentSlide === 0} />,
    beforeChange: (_current: number, next: number) => setCurrentSlide(next),
  };

  const handleOnClose = () => {
    closeDialog();
    setLoading(true);
  };

  useEffect(() => {
    setCurrentSlide(0);

    setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => {
      setLoading(true);
    };
  }, [closeDialog, isOpen]);

  if (dialogKey) {
    return (
      <Dialog
        key={dialogKey}
        maxWidth='700px'
        open={isOpen}
        title={undefined}
        onClose={handleOnClose}
        closeButton
      >
        <Box display='flex' flexDirection='column'>
          <Typography mt='none' variant='h3' color='primary/text' textAlign='center'>
            {t('title')}
          </Typography>
        </Box>

        <Box mb='xs' mt='xs' width='100%'>
          {loading && (
            <Box textAlign='center'>
              <Loader size='s' />
            </Box>
          )}
          {!loading && (
            <Card backgroundColor='grey/dark/background' p='xs'>
              {slides.length > 1 && (
                <Slider {...settings}>
                  {slides.map(slide => (
                    <Box mb='s' key={slide.assetName}>
                      <Box borderRadius='s' mb='xs'>
                        <Asset domain='images' width='100%' name={slide.assetName} />
                      </Box>
                      <Typography mb='xxs' variant='h4' fontSize='xs'>
                        {slide.title}
                      </Typography>
                      <Typography variant='p2/light' fontSize='xs'>
                        {slide.description}
                      </Typography>
                      <Typography variant='p2/light' mt='xs' fontSize='xs'>
                        <RenderLocalesWithComponents
                          text={tTutorial('goToGuide', {
                            goToGuideCta: '!{GO_TO_GUIDE_CTA}!',
                          })}
                          placeholders={{
                            '!{GO_TO_GUIDE_CTA}!': (
                              <Typography
                                as='span'
                                style={{
                                  cursor: 'pointer',
                                }}
                                color='primary/interactive'
                                onClick={() => goToPublicsDocs()}
                              >
                                {tTutorial('goToGuideCta')}
                              </Typography>
                            ),
                          }}
                        />
                      </Typography>
                    </Box>
                  ))}
                </Slider>
              )}
              {slides.length === 1 && (
                <Box mb='xxs'>
                  <Box mb='xs'>
                    <Asset domain='images' width='100%' name={slides[0].assetName} />
                  </Box>
                  <Typography mb='xxs' variant='h4' fontSize='xs'>
                    {slides[0].title}
                  </Typography>
                  <Typography variant='p2/light' fontSize='xs'>
                    {slides[0].description}
                  </Typography>
                  <Box display='flex' justifyContent='flex-end'>
                    <Button mt='xxs' variant='positive' onClick={handleOnClose} aria-label='Close'>
                      {tShared('close')}
                    </Button>
                  </Box>
                </Box>
              )}
            </Card>
          )}
        </Box>
      </Dialog>
    );
  }

  return null;
};
